'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { CATALOG_DEFAULT_RANKINGS, type CatalogDefaultRanking } from '@/libs/catalogo-default-rankings';
import type { InstrumentRow } from '@/components/catalogo/types';
import { useAppColors } from '@/styles/colors';
import { RankingCard, type RankingPreviewState } from './RankingCard';

type ActiveTrail = 'all' | CatalogDefaultRanking['category'];

const TRAIL_QUERY_KEY = 'trilha';
const TRAIL_QUERY_VALUE: Record<CatalogDefaultRanking['category'], string> = {
  dividendos: 'dividendos',
  liquidez: 'liquidez',
  patrimonio: 'patrimonio',
  valor: 'valor',
  selic: 'selic',
  alavancagem: 'alavancagem',
};
const TRAIL_BY_QUERY_VALUE: Record<string, ActiveTrail> = {
  dividendos: 'dividendos',
  liquidez: 'liquidez',
  patrimonio: 'patrimonio',
  valor: 'valor',
  selic: 'selic',
  alavancagem: 'alavancagem',
};

const TRAIL_CHIP_SX = {
  height: 32,
  borderRadius: 999,
  fontSize: '0.78rem',
  fontWeight: 800,
  px: 0.8,
  borderWidth: '1.5px',
};

const CATEGORY_META: Record<CatalogDefaultRanking['category'], { title: string; subtitle: string }> = {
  dividendos: {
    title: 'Dividendos',
    subtitle: 'Rankings focados em renda distribuída.',
  },
  liquidez: {
    title: 'Liquidez e base',
    subtitle: 'Rankings para entrada/saída e profundidade de base de investidores.',
  },
  patrimonio: {
    title: 'Patrimônio',
    subtitle: 'Rankings de escala e evolução patrimonial.',
  },
  valor: {
    title: 'Preço e qualidade',
    subtitle: 'Rankings para comparar desconto, distância da máxima e riscos operacionais.',
  },
  selic: {
    title: 'Sensibilidade à Selic',
    subtitle: 'Rankings para cenários de juros.',
  },
  alavancagem: {
    title: 'Alavancagem',
    subtitle: 'Rankings de exposição financeira.',
  },
};

const CATEGORY_ORDER: CatalogDefaultRanking['category'][] = [
  'dividendos',
  'liquidez',
  'patrimonio',
  'valor',
  'selic',
  'alavancagem',
];

const SORT_LABELS: Record<string, string> = {
  'dy12mPct:desc': 'DY 12m ↓',
  'numeroCotistas:desc': 'Cotistas ↓',
  'liquidezDiaria30d:desc': 'Liq. 30d ↓',
  'patrimonioLiquido:desc': 'Patrimônio ↓',
  'currentPvp:asc': 'P/VP ↑',
  'vpPerShareGrowth12mPct:desc': 'Cresc. VP/cota 12m ↓',
  'distanceTo12mHighPct:desc': 'Dist. máx. 12m ↓',
  'vacanciaPct:asc': 'Vacância ↑',
  'inadimplenciaPct:asc': 'Inadimplência ↑',
  'selicSensitivityScore:desc': 'Sensib. Selic ↓',
  'selicSensitivityScore:asc': 'Sensib. Selic ↑',
  'leverageIndexScore:desc': 'Alavancagem ↓',
  'leverageIndexScore:asc': 'Alavancagem ↑',
  'concentracaoTijoloPct:asc': 'Conc. Tijolo ↑',
  'concentracaoPapelPct:asc': 'Conc. Papel ↑',
};

type SortEntry = {
  field: string;
  direction: 'asc' | 'desc';
};

const parseSortEntries = (sort: string): SortEntry[] =>
  sort
    .split(',')
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .map((entry) => {
      const [fieldRaw, directionRaw] = entry.split(':');
      const field = (fieldRaw ?? '').trim();
      if (!field) return null;
      return {
        field,
        direction: (directionRaw ?? '').toLowerCase() === 'asc' ? 'asc' : 'desc',
      } as SortEntry;
    })
    .filter((value): value is SortEntry => value !== null);

const buildRankingEffectiveSort = (baseSort: string): string => {
  const entries = parseSortEntries(baseSort);
  const primary = entries[0] ?? { field: 'dy12mPct', direction: 'desc' as const };
  if (primary.field === 'dy12mPct') {
    return `${primary.field}:${primary.direction}`;
  }
  return `${primary.field}:${primary.direction},dy12mPct:desc`;
};

const formatSortLabel = (sort: string): string => {
  const entries = parseSortEntries(sort);
  if (entries.length === 0) return 'DY 12m ↓';
  return entries
    .map((entry) => SORT_LABELS[`${entry.field}:${entry.direction}`] ?? `${entry.field}:${entry.direction}`)
    .join(' · ');
};

const sortForPreview = (rows: InstrumentRow[], sort: string): InstrumentRow[] => {
  const sortEntries = parseSortEntries(sort);
  const effectiveEntries = sortEntries.length > 0 ? sortEntries : [{ field: 'dy12mPct', direction: 'desc' as const }];

  const getValue = (row: InstrumentRow, field: string): number | null => {
    if (field === 'dy12mPct') return row.dy12mPct ?? null;
    if (field === 'numeroCotistas') return row.numeroCotistas ?? null;
    if (field === 'liquidezDiaria30d') return row.liquidezDiaria30d ?? null;
    if (field === 'patrimonioLiquido') return row.patrimonioLiquido ?? null;
    if (field === 'currentPvp') return row.currentPvp ?? null;
    if (field === 'vpPerShareGrowth12mPct') return row.vpPerShareGrowth12mPct ?? null;
    if (field === 'distanceTo12mHighPct') return row.distanceTo12mHighPct ?? null;
    if (field === 'vacanciaPct') return row.vacanciaPct ?? null;
    if (field === 'inadimplenciaPct') return row.inadimplenciaPct ?? null;
    if (field === 'selicSensitivityScore') return row.selicSensitivityScore ?? null;
    if (field === 'leverageIndexScore') return row.leverageIndexScore ?? null;
    if (field === 'concentracaoTijoloPct') return row.concentracaoTijoloPct ?? null;
    if (field === 'concentracaoPapelPct') return row.concentracaoPapelPct ?? null;
    return null;
  };

  return [...rows].sort((a, b) => {
    for (const { field, direction } of effectiveEntries) {
      const aValue = getValue(a, field);
      const bValue = getValue(b, field);
      if (aValue === null && bValue === null) {
        continue;
      }
      if (aValue === null) return 1;
      if (bValue === null) return -1;
      if (aValue !== bValue) {
        if (direction === 'asc') return aValue - bValue;
        return bValue - aValue;
      }
    }
    return (a.idCem ?? '').localeCompare(b.idCem ?? '');
  });
};

export function RankingsClient() {
  const appColors = useAppColors();
  const router = useRouter();
  const pathname = usePathname();
  const safePathname = pathname ?? '/rankings-de-fiis';
  const searchParams = useSearchParams();
  const [previewById, setPreviewById] = useState<Record<string, RankingPreviewState>>({});

  const activeCategory = useMemo<ActiveTrail>(() => {
    const raw = (searchParams?.get(TRAIL_QUERY_KEY) ?? '').toLowerCase();
    return TRAIL_BY_QUERY_VALUE[raw] ?? 'all';
  }, [searchParams]);

  const updateTrailInUrl = useCallback(
    (nextTrail: ActiveTrail) => {
      const params = new URLSearchParams(searchParams?.toString() ?? '');
      if (nextTrail === 'all') {
        params.delete(TRAIL_QUERY_KEY);
      } else {
        params.set(TRAIL_QUERY_KEY, TRAIL_QUERY_VALUE[nextTrail]);
      }
      const nextQuery = params.toString();
      const currentQuery = searchParams?.toString() ?? '';
      if (nextQuery === currentQuery) return;
      router.replace(nextQuery ? `${safePathname}?${nextQuery}` : safePathname, {
        scroll: false,
      });
    },
    [router, safePathname, searchParams],
  );

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    setPreviewById(
      Object.fromEntries(
        CATALOG_DEFAULT_RANKINGS.map((ranking) => [
          ranking.id,
          { loading: true, error: null, rows: [], total: 0 } as RankingPreviewState,
        ]),
      ),
    );

    const load = async () => {
      await Promise.all(
        CATALOG_DEFAULT_RANKINGS.map(async (ranking) => {
          try {
            const params = new URLSearchParams(ranking.queryString ?? '');
            params.set('fundTypes', 'FII');
            const effectiveSort = buildRankingEffectiveSort(ranking.sort);
            params.set('sort', effectiveSort);
            const res = await fetch(`/api/catalogo/tickers?${params.toString()}`, {
              signal: controller.signal,
              cache: 'no-store',
            });
            if (!res.ok) {
              throw new Error(`Erro ao carregar ranking (${res.status})`);
            }
            const rows = (await res.json()) as InstrumentRow[];
            if (cancelled) return;
            const preview = sortForPreview(rows, effectiveSort).slice(0, 5);
            setPreviewById((current) => ({
              ...current,
              [ranking.id]: {
                loading: false,
                error: null,
                rows: preview,
                total: rows.length,
              },
            }));
          } catch (err: any) {
            if (cancelled || err?.name === 'AbortError') return;
            setPreviewById((current) => ({
              ...current,
              [ranking.id]: {
                loading: false,
                error: err?.message ?? 'Falha ao carregar ranking',
                rows: [],
                total: 0,
              },
            }));
          }
        }),
      );
    };

    load();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const groupedRankings = useMemo(
    () =>
      CATEGORY_ORDER.map((category) => ({
        category,
        ...CATEGORY_META[category],
        rankings: CATALOG_DEFAULT_RANKINGS.filter((ranking) => ranking.category === category),
      })).filter((section) => section.rankings.length > 0),
    [],
  );

  const visibleGroups = useMemo(
    () =>
      activeCategory === 'all' ? groupedRankings : groupedRankings.filter((group) => group.category === activeCategory),
    [activeCategory, groupedRankings],
  );

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, py: { xs: 1.5, md: 2 } }}>
      <Paper
        variant='outlined'
        sx={{
          p: { xs: 1.5, md: 2.25 },
          borderRadius: 2.5,
          backgroundColor: 'hsl(var(--card))',
          borderColor: 'hsl(var(--border))',
          maxWidth: 1780,
          mx: 'auto',
        }}
      >
        <Stack spacing={0.6} sx={{ mb: 2 }}>
          <Typography variant='h4' fontWeight={800} sx={{ fontSize: { xs: '1.8rem', md: '2.05rem' } }}>
            Rankings de FIIs
          </Typography>
          <Typography variant='body1' color='text.secondary' sx={{ maxWidth: 900 }}>
            Compare FIIs por ordenações objetivas. Todos os rankings já abrem filtrados em tipo de fundo FII.
          </Typography>
          <Stack direction='row' spacing={0.8} flexWrap='wrap' useFlexGap sx={{ pt: 0.4 }}>
            <Chip
              size='small'
              label='Todos'
              variant={activeCategory === 'all' ? 'filled' : 'outlined'}
              onClick={() => updateTrailInUrl('all')}
              sx={{
                ...TRAIL_CHIP_SX,
                ...(activeCategory === 'all'
                  ? {
                      backgroundColor: appColors.semantic.warning,
                      borderColor: appColors.semantic.warning,
                      color: appColors.actions.range.selectedText,
                      '&:hover': { backgroundColor: appColors.semantic.warningStrong },
                    }
                  : {
                      color: appColors.actions.range.unselectedText,
                      borderColor: appColors.actions.range.unselectedBorder,
                      '&:hover': {
                        color: appColors.actions.range.unselectedHoverText,
                        borderColor: appColors.actions.range.unselectedHoverBorder,
                        backgroundColor: appColors.actions.range.unselectedHoverBg,
                      },
                    }),
              }}
            />
            {CATEGORY_ORDER.map((category) => (
              <Chip
                key={category}
                size='small'
                label={CATEGORY_META[category].title}
                variant={activeCategory === category ? 'filled' : 'outlined'}
                onClick={() => updateTrailInUrl(category)}
                sx={{
                  ...TRAIL_CHIP_SX,
                  ...(activeCategory === category
                    ? {
                        backgroundColor: appColors.semantic.warning,
                        borderColor: appColors.semantic.warning,
                        color: appColors.actions.range.selectedText,
                        '&:hover': { backgroundColor: appColors.semantic.warningStrong },
                      }
                    : {
                        color: appColors.actions.range.unselectedText,
                        borderColor: appColors.actions.range.unselectedBorder,
                        '&:hover': {
                          color: appColors.actions.range.unselectedHoverText,
                          borderColor: appColors.actions.range.unselectedHoverBorder,
                          backgroundColor: appColors.actions.range.unselectedHoverBg,
                        },
                      }),
                }}
              />
            ))}
          </Stack>
        </Stack>

        <Stack spacing={2.2}>
          {visibleGroups.map((section) => (
            <Stack key={section.category} spacing={1.1}>
              <Stack spacing={0.2}>
                <Typography variant='h6' fontWeight={800} sx={{ fontSize: { xs: '1.02rem', md: '1.12rem' } }}>
                  {section.title}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {section.subtitle}
                </Typography>
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.4,
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))',
                    lg: 'repeat(3, minmax(0, 1fr))',
                    xl: 'repeat(4, minmax(0, 1fr))',
                  },
                }}
              >
                {section.rankings.map((ranking) => {
                  const effectiveSort = buildRankingEffectiveSort(ranking.sort);
                  return (
                    <RankingCard
                      key={ranking.id}
                      ranking={ranking}
                      effectiveSort={effectiveSort}
                      state={
                        previewById[ranking.id] ?? {
                          loading: true,
                          error: null,
                          rows: [],
                          total: 0,
                        }
                      }
                      sortLabel={formatSortLabel(effectiveSort)}
                    />
                  );
                })}
              </Box>
            </Stack>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
}
